import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Button, CheckCircleIcon, SpinnerWithBackground, Text, UploadIcon, WarningIcon } from '@viapeople/components'
import { useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useToast } from '../../../utils/ToastContext'
import { trpc } from '../../../utils/trpc'

/**
 * Flagged Words CSV Upload Component
 *
 * Allows CSMs to upload a CSV file containing flagged/inappropriate words.
 * The CSV can have 1-3 columns: word (required), category (optional), severity (optional).
 * File is stored at: organizations/{orgId}/customizations/flagged_words.csv
 */

const ALLOWED_FILE_TYPES = [
	'text/csv',
	'application/vnd.ms-excel',
	'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
] as const
const FILE_EXTENSIONS = '.csv, .xls, .xlsx'
const MAX_FILE_SIZE = 1 * 1024 * 1024 // 1MB

export function FlaggedWordsUpload() {
	const { organizationId } = useParams<{ organizationId: string }>()
	const toast = useToast()
	const queryClient = useQueryClient()
	const fileInputRef = useRef<HTMLInputElement>(null)
	const [selectedFile, setSelectedFile] = useState<File | null>(null)
	const [isUploading, setIsUploading] = useState(false)
	const [showPreview, setShowPreview] = useState(false)

	// Get current upload status
	const statusQuery = useQuery({
		...trpc.admin.flaggedWords.getStatus.queryOptions({
			organizationId: organizationId as string,
		}),
		enabled: !!organizationId,
	})

	// Get words for preview
	const wordsQuery = useQuery({
		...trpc.admin.flaggedWords.getWords.queryOptions({
			organizationId: organizationId as string,
		}),
		enabled: !!organizationId && showPreview,
	})

	// Request upload URL mutation
	const requestUploadMutation = useMutation({
		...trpc.admin.flaggedWords.requestUpload.mutationOptions(),
	})

	// Confirm upload mutation (called after S3 PUT succeeds to clear cache)
	const confirmUploadMutation = useMutation({
		...trpc.admin.flaggedWords.confirmUpload.mutationOptions(),
	})

	// Download URL query
	const downloadQuery = useQuery({
		...trpc.admin.flaggedWords.download.queryOptions({
			organizationId: organizationId as string,
		}),
		enabled: false, // Only fetch when explicitly requested
	})

	const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0]
		if (!file) return

		if (!(ALLOWED_FILE_TYPES as readonly string[]).includes(file.type) && !file.name.match(/\.(csv|xls|xlsx)$/i)) {
			toast.showToast('Invalid file type. Please upload a CSV or Excel file.', 'error')
			return
		}

		if (file.size > MAX_FILE_SIZE) {
			toast.showToast('File size exceeds 1MB limit.', 'error')
			return
		}

		setSelectedFile(file)
	}

	const handleUpload = async () => {
		if (!selectedFile || !organizationId) return

		setIsUploading(true)

		// Normalise MIME type by extension so backend enum validation always passes
		const normalisedMimetype = ((): (typeof ALLOWED_FILE_TYPES)[number] => {
			const name = selectedFile.name.toLowerCase()
			if (name.endsWith('.xlsx')) return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
			if (name.endsWith('.xls')) return 'application/vnd.ms-excel'
			return 'text/csv'
		})()

		try {
			// Step 1: Request presigned upload URL
			const uploadRequest = await requestUploadMutation.mutateAsync({
				organizationId,
				filename: selectedFile.name,
				mimetype: normalisedMimetype,
				size: selectedFile.size,
			})

			// Step 2: Upload file directly to S3
			const uploadResponse = await fetch(uploadRequest.uploadUrl, {
				method: 'PUT',
				headers: {
					'Content-Type': selectedFile.type,
				},
				body: selectedFile,
			})

			if (!uploadResponse.ok) {
				throw new Error('Failed to upload file to S3')
			}

			// Step 3: Confirm upload to clear server cache, then invalidate queries
			await confirmUploadMutation.mutateAsync({ organizationId })
			await queryClient.invalidateQueries({
				queryKey: trpc.admin.flaggedWords.getStatus.queryKey({ organizationId }),
			})

			toast.showToast('Flagged words file uploaded successfully')
			setSelectedFile(null)
			setShowPreview(false)

			// Reset file input
			if (fileInputRef.current) fileInputRef.current.value = ''
		} catch {
			toast.showToast('Failed to upload flagged words file', 'error')
		} finally {
			setIsUploading(false)
		}
	}

	const handleDownload = async () => {
		try {
			const result = await downloadQuery.refetch()
			if (result.data?.url) {
				window.open(result.data.url, '_blank')
			} else {
				toast.showToast('No file available for download', 'error')
			}
		} catch {
			toast.showToast('Failed to download file', 'error')
		}
	}

	const handleTogglePreview = () => {
		setShowPreview(!showPreview)
	}

	if (!organizationId) {
		return <Text>Organization ID not found</Text>
	}

	const isLoading = statusQuery.isLoading
	const fileExists = statusQuery.data?.exists || false

	return (
		<div className='p-6'>
			{isLoading ? (
				<SpinnerWithBackground />
			) : (
				<div className='space-y-6'>
					{/* Current Status */}
					<div className='flex items-center justify-between p-4 bg-neutral-50 rounded-lg border border-neutral-200'>
						<div className='flex items-center gap-3'>
							{fileExists ? (
								<CheckCircleIcon className='text-green-600 flex-shrink-0' size={20} />
							) : (
								<WarningIcon className='text-amber-500 flex-shrink-0' size={20} />
							)}
							<div>
								<Text className='font-semibold text-neutral-900'>
									{fileExists ? 'File Uploaded' : 'No File Uploaded'}
								</Text>
								<Text className='text-sm text-neutral-600'>
									{fileExists
										? 'Flagged words are currently active for this organization'
										: 'Upload a CSV file to start monitoring comments for flagged words'}
								</Text>
							</div>
						</div>
						{fileExists && (
							<div className='flex gap-2'>
								<Button onClick={handleTogglePreview} size='small' variant='secondary'>
									{showPreview ? 'Hide Preview' : 'Preview Words'}
								</Button>
								<Button onClick={handleDownload} size='small' variant='secondary'>
									Download
								</Button>
							</div>
						)}
					</div>

					{/* Preview Section */}
					{showPreview && wordsQuery.data && (
						<div className='p-4 bg-neutral-50 rounded-lg border border-neutral-200'>
							<Text className='font-semibold mb-3 text-neutral-900'>
								Flagged Words ({wordsQuery.data.words.length})
							</Text>
							<div className='max-h-64 overflow-auto space-y-1'>
								{wordsQuery.data.words.slice(0, 50).map((entry, index) => (
									<div key={`${entry.word}-${index}`} className='text-sm py-1'>
										<span className='font-medium text-neutral-900'>{entry.word}</span>
										{entry.category && <span className='text-neutral-600 ml-2'>({entry.category})</span>}
										{entry.severity && (
											<span className='text-neutral-500 text-xs ml-2 px-2 py-0.5 bg-neutral-200 rounded'>
												{entry.severity}
											</span>
										)}
									</div>
								))}
								{wordsQuery.data.words.length > 50 && (
									<Text className='text-sm text-neutral-500 mt-3 italic'>
										... and {wordsQuery.data.words.length - 50} more
									</Text>
								)}
							</div>
						</div>
					)}

					{/* Upload Section */}
					<div className='space-y-3'>
						<div className='relative'>
							<label
								htmlFor='flagged-words-file-input'
								className='flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-neutral-300 rounded-lg hover:border-primary-500 hover:bg-primary-50 transition-colors cursor-pointer'>
								<UploadIcon size={20} className='text-neutral-600' />
								<Text className='text-sm font-medium text-neutral-700'>
									{selectedFile ? selectedFile.name : 'Choose CSV or Excel file or drag and drop'}
								</Text>
							</label>
							<input
								ref={fileInputRef}
								id='flagged-words-file-input'
								type='file'
								accept={FILE_EXTENSIONS}
								onChange={handleFileSelect}
								className='absolute inset-0 w-full h-full opacity-0 cursor-pointer'
								disabled={isUploading}
							/>
						</div>

						{selectedFile && (
							<div className='flex items-center justify-between p-4 bg-primary-50 rounded-lg border border-primary-200'>
								<div className='flex items-center gap-3'>
									<UploadIcon size={20} className='text-primary-600' />
									<div>
										<Text className='text-sm font-semibold text-primary-900'>{selectedFile.name}</Text>
										<Text className='text-xs text-primary-700'>{(selectedFile.size / 1024).toFixed(2)} KB</Text>
									</div>
								</div>
								<Button onClick={handleUpload} disabled={isUploading} size='small'>
									{isUploading ? 'Uploading...' : 'Upload'}
								</Button>
							</div>
						)}

						<Text className='text-xs text-neutral-500'>
							<span className='font-medium'>Format:</span> CSV or Excel (.xls, .xlsx) file. First column = word
							(required), second column = category (optional), third column = severity (optional). Header row is
							optional. Maximum file size: 1MB
						</Text>
					</div>
				</div>
			)}
		</div>
	)
}
